import React, { useState } from "react"
import { Box, IconButton, Typography } from "@mui/material"
import { ExpandLess, ExpandMore } from "@mui/icons-material"
import Collapse from "@mui/material/Collapse"
import OptionTileSelector from "../OptionTileSelector"

// Board outline + actual patch paths from PerformanceSquashSvg
const O = { stroke: "currentColor", fill: "none", strokeWidth: 3 }
const BOARD = "M100.7,914.5c0,0-55.8-176.5-55.8-412.8c0-381,120.005-481.915,120.005-481.915s120.01,98.786,120.01,479.577c0,268.7-58.8,415.2-58.8,415.2s-6.4,23.4-43.8,23.4s-43.7,0-43.7,0S106.4,934.1,100.7,914.5z"

const PatchTile = ({ patchD }) => (
  <svg viewBox="20 0 290 980" width="30" height="55">
    <path d={BOARD} {...O} />
    {patchD && <path d={patchD} fill="currentColor" fillOpacity={0.3} stroke="currentColor" strokeWidth={1.5} />}
  </svg>
)

const DeckPatchSvgs = {
  None: () => <PatchTile />,
  "Straight Point": () => <PatchTile patchD="M100.915,914.895 c0,0-59.673-195.5-49.672-518.375l113.178-80.528l114.226,80.528c11.43,372.041-52.569,518.375-52.569,518.375s-9.666,23-47,23 s-34.483,0-34.483,0S110.421,936.895,100.915,914.895z" />,
  "Curved Point": () => <PatchTile patchD="M100.746,914.464 c0,0-63.504-223.235-49.15-517.944c0,0,41.487-9.652,72.761-34.167s40.918-60.931,40.918-60.931s12.188,36.501,38.186,59.53 c28.464,25.213,74.537,35.567,74.537,35.567c15.244,315.209-51.813,517.981-51.813,517.981s-9.109,23.394-45.776,23.394 s-35.817,0-35.817,0S111.75,938.367,100.746,914.464z" />,
  Round: () => <PatchTile patchD="M100.746,914.464 c0,0-61.504-204.735-49.503-517.944c0.001,0,102.697-181.19,227.403,0c13.596,318.709-52.461,517.982-52.461,517.982 s-9.443,23.393-45.776,23.393s-31.974,0.104-31.974,0.104S114.75,938.367,100.746,914.464z" />,
}

const TailPatchSvgs = {
  None: () => <PatchTile />,
  "Straight Point": () => <PatchTile patchD="M101.542,914.473 c0,0-25.299-73.744-42.069-226.88l105.837-30.604l105.244,30.604c-15.313,153.136-44.943,226.909-44.943,226.909 s-10.18,23.393-45.688,23.393s-34.685,0-34.685,0S111.027,937.385,101.542,914.473z" />,
  "Reverse Straight Point": () => <PatchTile patchD="M101.542,914.473 c0,0-26.299-79.744-41.135-226.88l104.903,44.734l104.309-44.734c-11.667,149.636-44.008,226.909-44.008,226.909 s-8.889,23.495-47.625,23.495s-29.026,0-29.026,0S112.318,940.051,101.542,914.473z" />,
  "Curved Point": () => <PatchTile patchD="M101.542,914.473 c0,0-25.299-68.778-42.069-226.88c0,0,49.373-2.226,76.566-15.644c27.193-13.416,29.271-22.224,29.271-22.224 s10.629,16.109,24.625,21.562c19.492,7.592,80.685,16.306,80.685,16.306c-12.668,141.796-47.009,226.909-47.009,226.909 s-7.533,23.393-45.625,23.393c-38.09,0-32.749,0-32.749,0S110.704,937.051,101.542,914.473z" />,
  "Reverse Curved Point": () => <PatchTile patchD="M101.542,914.473 c0,0-27.299-78.744-41.597-227.553c0,0,72.533-3.903,86.53,16.223c13.995,20.128,19.501,34.094,19.501,34.094 s8.119-18.4,27.584-34.106c20.279-16.358,76.498-16.208,76.498-16.208c-9.315,142.468-44.447,227.582-44.447,227.582 s-7.92,23.393-51.176,23.393c-43.256,0-21.951,0-21.951,0S114.901,941.718,101.542,914.473z" />,
  Round: () => <PatchTile patchD="M101.542,914.473 c0,0-23.799-64.744-40.62-205.85c-0.821-6.795-0.682-13.804-1.447-21.032c-0.001,0.002,101.658-68.765,210.144,0.002 c0,0,0.716,12.001,0,17.855c-17.376,148.778-44.008,209.054-44.008,209.054s-8.889,23.394-41.491,23.394 c-32.602,0-31.635,0-31.635,0S111.996,942.051,101.542,914.473z" />,
}

const FiberGlass = ({
  updateOptions,
  setSelectedDeckPatchOption,
  deckPatchOptions,
  selectedDeckPatchOption,
  selectedTailPatchOption,
  setSelectedTailPatchOption,
  tailPatchOptions,
  orderData,
  isStaff,
}) => {
  const [open, setOpen] = useState(true)

  return (
    <React.Fragment>
      <Box pt={3} display="flex" alignItems="center" justifyContent="center" sx={{ position: "relative" }}>
        <Typography variant="h5">Fiberglass</Typography>
        <IconButton onClick={() => setOpen(!open)} sx={{ position: "absolute", right: 0 }}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <OptionTileSelector
          isStaff={isStaff}
          orderData={orderData}
          updateOptions={updateOptions}
          title={"Deck Patch"}
          option={selectedDeckPatchOption}
          options={deckPatchOptions}
          svgMap={DeckPatchSvgs}
        />
        <OptionTileSelector
          isStaff={isStaff}
          orderData={orderData}
          updateOptions={updateOptions}
          title={"Tail Patch"}
          option={selectedTailPatchOption}
          options={tailPatchOptions}
          svgMap={TailPatchSvgs}
        />
      </Collapse>
    </React.Fragment>
  )
}

export default FiberGlass
